import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
    Button,
    Snackbar,
    SnackbarContent,
    Backdrop,
    CircularProgress,
    Typography,
    Paper,
    Stack,
    Switch,
    FormControlLabel,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import UpdateIcon from '@mui/icons-material/Update';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

export const UPDATE_DEBUG = false;

const LAST_POPUP_KEY = 'lastUpdatePopup';
const DAY_MS = 24 * 60 * 60 * 1000;
const SHOW_DELAY = 500;
const CHECK_DELAY = 2000;
const FORCE_SPINNER = 4000;
const REOPEN_DELAY = 100;

const setCurrentDate = () => {
    try {
        localStorage.setItem(LAST_POPUP_KEY, String(Date.now()));
    } catch {}
};

const startUpdate = () => {
    setCurrentDate();
    window.location.reload();
};

const checkForUpdateDate = () => {
    let last = null;
    try {
        const lastShown = localStorage.getItem(LAST_POPUP_KEY);
        if (lastShown) last = Number(lastShown);
    } catch {}
    const now = Date.now();
    if (!last) return true;
    if (last > now) {
        try {
            localStorage.removeItem(LAST_POPUP_KEY);
        } catch {}
        return true;
    }
    const diff = now - last;
    if (diff > 3 * DAY_MS) return 'forceUpdateRequired';
    if (diff > DAY_MS) return true;
    return false;
};

const requestSwUpdate = () => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.getRegistration().then(reg => reg?.update());
};

export default function UpdateComponent() {
    const { t } = useTranslation();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [open, setOpen] = useState(false);
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [loading, setLoading] = useState(true);

    const debugEnabled = useMemo(() => {
        if (UPDATE_DEBUG) return true;
        const params = new URLSearchParams(location?.search || '');
        const flag = params.get('updateDebug');
        return flag === '1' || flag === 'true';
    }, [location?.search]);

    const [debugState, setDebugState] = useState({
        open: true,
        loading: true,
        updateAvailable: true,
    });

    const timersRef = useRef(new Set());
    const setT = useCallback((fn, ms) => {
        const id = setTimeout(fn, ms);
        timersRef.current.add(id);
        return id;
    }, []);
    const clearAllT = useCallback(() => {
        timersRef.current.forEach(id => clearTimeout(id));
        timersRef.current.clear();
    }, []);

    const performUpdateUI = useCallback(
        type => {
            if (type === 'forceUpdateRequired') {
                setLoading(true);
                setT(() => {
                    setOpen(false);
                    setT(() => {
                        setOpen(true);
                        setLoading(false);
                        setUpdateAvailable(true);
                    }, REOPEN_DELAY);
                }, FORCE_SPINNER);
            } else if (type === true) {
                setT(() => {
                    setCurrentDate();
                    setOpen(false);
                }, 2000);
            }
        },
        [setT]
    );

    const checkAndHandleUpdate = useCallback(() => {
        clearAllT();
        const status = checkForUpdateDate();
        if (!status) return;

        setT(() => setOpen(true), SHOW_DELAY);

        setT(async () => {
            const reg = await navigator.serviceWorker?.getRegistration();
            if (reg?.waiting || reg?.installing) return;
            clearAllT();
            performUpdateUI(status);
        }, CHECK_DELAY);
    }, [clearAllT, performUpdateUI, setT]);

    useEffect(() => {
        if (debugEnabled) return;
        const decision = checkForUpdateDate();
        if (decision) {
            requestSwUpdate();
            checkAndHandleUpdate();
        }
    }, [checkAndHandleUpdate, debugEnabled, location?.pathname]);

    useEffect(() => {
        if (debugEnabled) return;
        let swRegUnmountCleanup = () => {};
        let msgHandler = null;

        const init = async () => {
            const registration = await navigator.serviceWorker?.getRegistration();
            if (!registration) return;

            const showUpdateUI = () => {
                clearAllT();
                setOpen(false);
                setT(() => {
                    setUpdateAvailable(true);
                    setOpen(true);
                    setLoading(false);
                }, REOPEN_DELAY);
            };

            if (registration.waiting) showUpdateUI();

            const updateFoundHandler = () => {
                const newWorker = registration.installing;
                if (!newWorker) return;
                const stateHandler = () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker?.controller) {
                        showUpdateUI();
                    }
                };
                newWorker.addEventListener('statechange', stateHandler);
                swRegUnmountCleanup = () => newWorker.removeEventListener('statechange', stateHandler);
            };

            registration.addEventListener('updatefound', updateFoundHandler);
            swRegUnmountCleanup = () => registration.removeEventListener('updatefound', updateFoundHandler);
        };

        init();

        msgHandler = event => {
            if (event?.data?.type === 'RELOAD_PAGE') startUpdate();
        };
        navigator.serviceWorker?.addEventListener('message', msgHandler);

        return () => {
            swRegUnmountCleanup?.();
            if (msgHandler) navigator.serviceWorker?.removeEventListener('message', msgHandler);
            clearAllT();
        };
    }, [clearAllT, debugEnabled, setT]);

    const handleUpdate = useCallback(() => {
        if (debugEnabled) {
            setDebugState(prev => ({ ...prev, open: false }));
            return;
        }
        if (!navigator.serviceWorker) {
            startUpdate();
            return;
        }
        navigator.serviceWorker
            .getRegistration()
            .then(registration => {
                if (!registration) {
                    if (navigator.serviceWorker.controller) {
                        navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
                    }
                    startUpdate();
                    return;
                }
                if (registration.waiting) {
                    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                } else if (navigator.serviceWorker.controller) {
                    navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
                }
            })
            .catch(() => {});
    }, [debugEnabled]);

    const isOpen = debugEnabled ? debugState.open : open;
    const isLoading = debugEnabled ? debugState.loading : loading;
    const isUpdateAvailable = debugEnabled ? debugState.updateAvailable : updateAvailable;

    const snackbar = (
        <Snackbar
            open={isOpen}
            anchorOrigin={{ vertical: isMobile ? 'bottom' : 'top', horizontal: 'center' }}
        >
            <SnackbarContent
                sx={{ bgcolor: '#fff', color: '#000' }}
                message={
                    <>
                        <Typography
                            sx={{ fontSize: isLoading ? '14px' : '18px' }}
                            flex={1}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={2}
                        >
                            {isLoading ? 'Пошук оновлення...' : t('notifications.updateAlaviable')}
                            {isLoading ? <CircularProgress size={20} /> : null}
                        </Typography>
                        {!isLoading && (
                            <Typography
                                variant="caption"
                                sx={{ color: 'rgb(246 73 73)', fontWeight: 500, width: 300, display: 'block', mt: 0.5 }}
                            >
                                {t('notifications.updateWarning')}
                            </Typography>
                        )}
                    </>
                }
                action={
                    !isLoading && isUpdateAvailable ? (
                        <Button
                            sx={{ textTransform: 'capitalize' }}
                            variant="contained"
                            color="primary"
                            onClick={handleUpdate}
                            endIcon={<UpdateIcon />}
                            style={{
                                padding: '5px 20px',
                                borderRadius: '8px',
                            }}
                        >
                            {t('notifications.update')}
                        </Button>
                    ) : null
                }
            />
        </Snackbar>
    );

    const debugPanel = debugEnabled ? (
        <Paper
            elevation={6}
            sx={{
                position: 'fixed',
                top: 8,
                left: 8,
                right: 8,
                zIndex: 1405,
                p: 1,
            }}
        >
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Update debug
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={debugState.open}
                            onChange={event => setDebugState(prev => ({ ...prev, open: event.target.checked }))}
                        />
                    }
                    label="Open"
                />
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={debugState.loading}
                            onChange={event => setDebugState(prev => ({ ...prev, loading: event.target.checked }))}
                        />
                    }
                    label="Loading"
                />
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={debugState.updateAvailable}
                            onChange={event => setDebugState(prev => ({ ...prev, updateAvailable: event.target.checked }))}
                        />
                    }
                    label="Update available"
                />
            </Stack>
        </Paper>
    ) : null;

    return (
        <>
            {debugPanel}
            {isLoading ? (
                <div>{snackbar}</div>
            ) : (
                <Backdrop open={isOpen && !isLoading} sx={{ zIndex: 1300, cursor: 'not-allowed' }}>
                    {snackbar}
                </Backdrop>
            )}
        </>
    );
}
